import type { ExpressionSpecification } from 'mapbox-gl';
import * as MapGL from 'react-map-gl/mapbox';

export const MAP_HIGHLIGHT_SOURCE = 'MAP_HIGHLIGHT_SOURCE';
export const HIGHLIGHT_LINE_LAYER_ID = 'highlight-line';
export const HIGHLIGHT_CIRCLE_LAYER_ID = 'highlight-circle';
export const CUSTOM_DATA_RASTER_ANCHOR_ID = 'custom-data-raster-anchor';
export const GEOREFERENCED_MAPS_ANCHOR_ID = 'georeferenced-maps-anchor';
export const MAP_HOVER_HIGHLIGHT_SOURCE = 'MAP_HOVER_HIGHLIGHT_SOURCE';
// Core (top) layers keep the original ids; glow + casing are the added under-layers.
export const HOVER_HIGHLIGHT_LINE_LAYER_ID = 'hover-highlight-line';
export const HOVER_HIGHLIGHT_LINE_GLOW_LAYER_ID = 'hover-highlight-line-glow';
export const HOVER_HIGHLIGHT_LINE_CASING_LAYER_ID = 'hover-highlight-line-casing';
export const HOVER_HIGHLIGHT_CIRCLE_LAYER_ID = 'hover-highlight-circle';
export const HOVER_HIGHLIGHT_CIRCLE_GLOW_LAYER_ID = 'hover-highlight-circle-glow';
export const HOVER_HIGHLIGHT_CIRCLE_CASING_LAYER_ID = 'hover-highlight-circle-casing';
export const HOVER_HIGHLIGHT_POLYGON_FILL_LAYER_ID = 'hover-highlight-polygon-fill';
export const HOVER_HIGHLIGHT_POLYGON_GLOW_LAYER_ID = 'hover-highlight-polygon-glow';
export const HOVER_HIGHLIGHT_POLYGON_CASING_LAYER_ID = 'hover-highlight-polygon-casing';
export const HOVER_HIGHLIGHT_POLYGON_CORE_LAYER_ID = 'hover-highlight-polygon-core';
export const MAP_CLICK_REFERENCE_SOURCE = 'MAP_CLICK_REFERENCE_SOURCE';
export const CLICK_REFERENCE_CIRCLE_LAYER_ID = 'click-reference-circle';

// On-map highlight = the asset in its own utility color + a white casing + a blue-80 (#243442)
// outer glow (Seth's spec). Line widths widened for prominence when zoomed in (Shane). Still tunable.
export const GLOW_COLOR = '#243442'; // blue-80 outer glow/outline
export const CASING_COLOR = '#FFFFFF'; // crisp white casing between the glow and the core
const CORE_COLOR: ExpressionSpecification = ['coalesce', ['get', 'color'], '#243442']; // the asset's utility color; blue-80 fallback
const GLOW_OPACITY = 1; // Seth's copied value; glow, casing, and core are all fully opaque
// Widths/radii are [z10, z18] endpoints, zoom-interpolated below. Band proportions follow Seth's
// 3:242 mockup: a thin utility-colored core, a DOMINANT white gap, and a thin blue-80 outer edge.
export const LINE_GLOW_WIDTH: [number, number] = [26, 17];
export const LINE_CASING_WIDTH: [number, number] = [22, 14];
export const LINE_CORE_WIDTH: [number, number] = [8, 4]; // narrower than the casing at both zooms
export const LINE_GLOW_BLUR = 0; // PIXELS (line-blur unit); Seth's copied value is 0 (crisp, no blur)
// Point radii: fixed screen-px (no zoom taper). White casing is the dominant band per Seth's 3:242
// mockup — a 4px utility-colored core, a ~6px white gap, and a thin ~2px blue-80 outer edge. Tunable.
const CIRCLE_GLOW_RADIUS: [number, number] = [12, 12];
const CIRCLE_CASING_RADIUS: [number, number] = [10, 10];
const CIRCLE_CORE_RADIUS: [number, number] = [4, 4]; // utility core; white casing dominant, thin blue-80 edge
const CIRCLE_GLOW_BLUR = 0; // RATIO of the radius (circle-blur unit), NOT pixels; Seth's copied value is 0
export const POLYGON_FILL_OPACITY = 0.15; // translucent tint in the asset's own color for a selected/hovered polygon interior (tunable)

// Reference circle marking the clicked point — values are placeholders, tune with design
const REFERENCE_CIRCLE_RADIUS = 16; // fixed screen px
const REFERENCE_CIRCLE_FILL_COLOR = '#c0dbfe'; // light blue
const REFERENCE_CIRCLE_FILL_OPACITY = 0.18;
const REFERENCE_CIRCLE_STROKE_COLOR = '#242d39'; // dark navy
const REFERENCE_CIRCLE_STROKE_WIDTH = 2;
const REFERENCE_CIRCLE_STROKE_OPACITY = 1;

// z10→z18 exponential interpolation for a zoom-dependent paint value. THE curve — the
// feature-state stack builds its own widths on this same function (featureStateZoomInterp) so the
// interpolation base and the z10/z18 stops can never drift apart between the two highlight paths.
// Stop outputs are expressions as well as numbers for that caller, which gates each one on feature
// state; `['zoom']` may only be the input of a top-level interpolate, so the gate has to live in
// the stop output rather than wrap this.
export const zoomInterp = (
  z10: number | ExpressionSpecification,
  z18: number | ExpressionSpecification,
): ExpressionSpecification => ['interpolate', ['exponential', 1.5], ['zoom'], 10, z10, 18, z18];

export default function () {
  return (
    <>
      {/* Invisible anchor at the bottom of the CG layer stack; the WMTS raster sits just below it
          (above the basemap, below every CG TOC layer + highlight). Must render before highlight-line. */}
      <MapGL.Layer id={CUSTOM_DATA_RASTER_ANCHOR_ID} type="background" layout={{ visibility: 'none' }} />
      {/* Geo-referenced map PNGs sit just below this one: above the WMTS raster, below every CG TOC layer. */}
      <MapGL.Layer id={GEOREFERENCED_MAPS_ANCHOR_ID} type="background" layout={{ visibility: 'none' }} />
      <MapGL.Source
        id={MAP_HIGHLIGHT_SOURCE}
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: [],
        }}
      >
        <MapGL.Layer
          id={HIGHLIGHT_LINE_LAYER_ID}
          type="line"
          filter={['==', '$type', 'LineString']}
          paint={{
            'line-color': 'white',
            'line-width': 25,
            'line-opacity': 0.5,
          }}
          layout={{ 'line-cap': 'round' }}
        />
        <MapGL.Layer
          id={HIGHLIGHT_CIRCLE_LAYER_ID}
          type="circle"
          filter={['==', '$type', 'Point']}
          paint={{
            'circle-color': 'white',
            'circle-radius': 25,
            'circle-opacity': 0.5,
          }}
        />
      </MapGL.Source>
      {/* Reference circle at the clicked point. Rendered before the hover source so it sits UNDER
          the hover glow when they overlap. Fed by MetadataSidebar via identifyDetailsPoint. */}
      <MapGL.Source
        id={MAP_CLICK_REFERENCE_SOURCE}
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: [],
        }}
      >
        <MapGL.Layer
          id={CLICK_REFERENCE_CIRCLE_LAYER_ID}
          type="circle"
          filter={['==', '$type', 'Point']}
          paint={{
            'circle-radius': REFERENCE_CIRCLE_RADIUS,
            'circle-color': REFERENCE_CIRCLE_FILL_COLOR,
            'circle-opacity': REFERENCE_CIRCLE_FILL_OPACITY,
            'circle-stroke-color': REFERENCE_CIRCLE_STROKE_COLOR,
            'circle-stroke-width': REFERENCE_CIRCLE_STROKE_WIDTH,
            'circle-stroke-opacity': REFERENCE_CIRCLE_STROKE_OPACITY,
          }}
        />
      </MapGL.Source>
      <MapGL.Source
        id={MAP_HOVER_HIGHLIGHT_SOURCE}
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: [],
        }}
      >
        {/* Layered "glow" stack — JSX order is draw order (bottom → top): glow, white casing, colored core. */}
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_LINE_GLOW_LAYER_ID}
          type="line"
          filter={['==', '$type', 'LineString']}
          paint={{
            'line-color': GLOW_COLOR,
            'line-width': zoomInterp(...LINE_GLOW_WIDTH),
            'line-blur': LINE_GLOW_BLUR,
            'line-opacity': GLOW_OPACITY,
          }}
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
        />
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_LINE_CASING_LAYER_ID}
          type="line"
          filter={['==', '$type', 'LineString']}
          paint={{
            'line-color': CASING_COLOR,
            'line-width': zoomInterp(...LINE_CASING_WIDTH),
          }}
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
        />
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_LINE_LAYER_ID}
          type="line"
          filter={['==', '$type', 'LineString']}
          paint={{
            'line-color': CORE_COLOR,
            'line-width': zoomInterp(...LINE_CORE_WIDTH),
          }}
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
        />
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_CIRCLE_GLOW_LAYER_ID}
          type="circle"
          filter={['==', '$type', 'Point']}
          paint={{
            'circle-color': GLOW_COLOR,
            'circle-radius': zoomInterp(...CIRCLE_GLOW_RADIUS),
            'circle-blur': CIRCLE_GLOW_BLUR,
            'circle-opacity': GLOW_OPACITY,
          }}
        />
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_CIRCLE_CASING_LAYER_ID}
          type="circle"
          filter={['==', '$type', 'Point']}
          paint={{
            'circle-color': CASING_COLOR,
            'circle-radius': zoomInterp(...CIRCLE_CASING_RADIUS),
          }}
        />
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_CIRCLE_LAYER_ID}
          type="circle"
          filter={['==', '$type', 'Point']}
          paint={{
            'circle-color': CORE_COLOR,
            'circle-radius': zoomInterp(...CIRCLE_CORE_RADIUS),
          }}
        />
        {/* Polygon (parcel) highlight: translucent fill + a glowing outline. The source holds one
            feature at a time, so this never overlaps the line/circle stacks. */}
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_POLYGON_FILL_LAYER_ID}
          type="fill"
          filter={['==', '$type', 'Polygon']}
          paint={{
            'fill-color': CORE_COLOR,
            'fill-opacity': POLYGON_FILL_OPACITY,
          }}
        />
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_POLYGON_GLOW_LAYER_ID}
          type="line"
          filter={['==', '$type', 'Polygon']}
          paint={{
            'line-color': GLOW_COLOR,
            'line-width': zoomInterp(...LINE_GLOW_WIDTH),
            'line-blur': LINE_GLOW_BLUR,
            'line-opacity': GLOW_OPACITY,
          }}
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
        />
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_POLYGON_CASING_LAYER_ID}
          type="line"
          filter={['==', '$type', 'Polygon']}
          paint={{
            'line-color': CASING_COLOR,
            'line-width': zoomInterp(...LINE_CASING_WIDTH),
          }}
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
        />
        <MapGL.Layer
          id={HOVER_HIGHLIGHT_POLYGON_CORE_LAYER_ID}
          type="line"
          filter={['==', '$type', 'Polygon']}
          paint={{
            'line-color': CORE_COLOR,
            'line-width': zoomInterp(...LINE_CORE_WIDTH),
          }}
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
        />
      </MapGL.Source>
    </>
  );
}
